import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";

// -----------------------------------------------------------------------------
// CityTaxCard — Imposta di soggiorno configuration per property. Kept separate
// from the pricing/commission math so this line stays out of the finance KPI
// aggregation (gross/net/commission all ignore city_tax by design).
// Backend model: `properties.city_tax` = {
//   enabled, amount_per_person_per_night, max_nights, exempt_under_age
// }
// -----------------------------------------------------------------------------
export default function CityTaxCard({ property, onSaved }) {
  const { lang } = useLang();
  const IT = lang === "it";
  const [form, setForm] = useState({
    enabled: false,
    amount_per_person_per_night: 2,
    max_nights: 7,
    exempt_under_age: 10,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const ct = property?.city_tax || {};
    setForm({
      enabled: !!ct.enabled,
      amount_per_person_per_night: ct.amount_per_person_per_night ?? 2,
      max_nights: ct.max_nights ?? 7,
      exempt_under_age: ct.exempt_under_age ?? 10,
    });
  }, [property?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (patch = {}) => {
    setSaving(true);
    try {
      const merged = { ...form, ...patch };
      // Normalise numeric inputs before persist.
      const payload = {
        enabled: !!merged.enabled,
        amount_per_person_per_night: Math.max(0, Number(merged.amount_per_person_per_night) || 0),
        max_nights: Math.max(0, parseInt(merged.max_nights, 10) || 0),
        exempt_under_age: Math.max(0, parseInt(merged.exempt_under_age, 10) || 0),
      };
      await api.put("/properties/me", { city_tax: payload });
      setForm(payload);
      toast.success(IT ? "Imposta di soggiorno aggiornata" : "City tax settings saved");
      onSaved?.();
    } catch (e) {
      toast.error(errMsg(e, IT ? "Errore" : "Error"));
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-border rounded-lg p-4 bg-card space-y-3" data-testid="city-tax-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-primary"/>
          <div>
            <Label className="mb-0 text-sm">{IT ? "Imposta di soggiorno" : "City tax"}</Label>
            <p className="text-[11px] text-muted-foreground">
              {IT
                ? "Contabilizzata separatamente dal fatturato — resta fuori da commissioni HH e IVA."
                : "Tracked separately from revenue — excluded from HH commission and VAT."}
            </p>
          </div>
        </div>
        <Switch
          checked={form.enabled}
          onCheckedChange={(v) => save({ enabled: v })}
          data-testid="city-tax-enabled"
          aria-label={IT ? "Attiva imposta di soggiorno" : "Enable city tax"}
        />
      </div>

      {form.enabled && (
        <div className="grid grid-cols-3 gap-3 pt-1">
          <div>
            <Label className="text-[11px]">{IT ? "€/persona/notte" : "€/person/night"}</Label>
            <Input
              type="number"
              min={0}
              step={0.5}
              value={form.amount_per_person_per_night}
              onChange={(e) => setForm({ ...form, amount_per_person_per_night: e.target.value })}
              onBlur={() => save()}
              data-testid="city-tax-amount"
              className="font-mono"
            />
          </div>
          <div>
            <Label className="text-[11px]">{IT ? "Notti max" : "Max nights"}</Label>
            <Input
              type="number"
              min={0}
              step={1}
              value={form.max_nights}
              onChange={(e) => setForm({ ...form, max_nights: e.target.value })}
              onBlur={() => save()}
              placeholder="0 = ∞"
              data-testid="city-tax-max-nights"
              className="font-mono"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">{IT ? "0 = illimitate" : "0 = unlimited"}</p>
          </div>
          <div>
            <Label className="text-[11px]">{IT ? "Esente sotto anni" : "Exempt under age"}</Label>
            <Input
              type="number"
              min={0}
              max={30}
              step={1}
              value={form.exempt_under_age}
              onChange={(e) => setForm({ ...form, exempt_under_age: e.target.value })}
              onBlur={() => save()}
              placeholder="0 = nessuna"
              data-testid="city-tax-exempt-age"
              className="font-mono"
            />
            <p className="text-[10px] text-muted-foreground mt-0.5">{IT ? "0 = nessuna esenzione" : "0 = no exemption"}</p>
          </div>
        </div>
      )}

      {form.enabled && (
        <p className="text-[10px] text-muted-foreground italic">
          {IT
            ? `Preview: 2 adulti × 3 notti = € ${(2 * Math.min(3, Number(form.max_nights) || 3) * (Number(form.amount_per_person_per_night) || 0)).toFixed(2)}. Ricalcolato ad ogni check-in in base all'età reale.`
            : `Preview: 2 adults × 3 nights = € ${(2 * Math.min(3, Number(form.max_nights) || 3) * (Number(form.amount_per_person_per_night) || 0)).toFixed(2)}. Recomputed at each check-in based on real ages.`}
        </p>
      )}
      {saving && <div className="text-[10px] text-muted-foreground">{IT ? "Salvataggio…" : "Saving…"}</div>}
    </div>
  );
}
